import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles, Users, Heart, ShieldCheck } from 'lucide-react';
import SEO from '../../components/SEO';
import Section from '../../components/v2/Section';
import LatestFromBlog from '../../components/v2/LatestFromBlog';
import { PrimaryCTA, CTABlock } from '../../components/v2/CTAButton';
import { PORTFOLIO_ITEMS } from '../../components/v2/PortfolioGrid';

const JO_PHOTO = '/images/jo-day.jpg';

// Small trust badges, quick facts, not a CV.
const TRUST = [
  {
    icon: Heart,
    t: 'Independent studio',
    d: 'Not a faceless agency. A small team who knows what it takes to run a business.',
  },
  {
    icon: ShieldCheck,
    t: '16 years in business',
    d: 'Getting up and doing the do, every day since 2010. We understand the hustle because we live it.',
  },
  {
    icon: Users,
    t: '120+ podcasts launched',
    d: 'Clients ranking in the top 10% worldwide. End-to-end production done for you.',
  },
  {
    icon: Sparkles,
    t: 'Lean Six Sigma trained',
    d: 'The systems Fortune 500s use, applied to the reality of small business.',
  },
];

// Narrative "how we got here", not a chronological CV. Each beat ties back to WHY.
const STORY_BEATS = [
  {
    head: '2010, I became a business owner.',
    body:
      'And I learnt, painfully, that being brilliant at your craft is only 20% of the job. The other 80% is being seen, being heard, and being remembered. Nobody teaches you that bit.',
  },
  {
    head: '2010 to 2018, Business Management Consultant, UK & Irish energy and water markets.',
    body:
      'Eight years advising challenger brands and city councils at MD and C-suite level. Lean Six Sigma processes, growth strategy, operational turnarounds. The client work that funded (and fuelled) everything that came next, and taught me exactly what small business owners are up against once they leave the corporate world.',
  },
  {
    head: '2018, First self-published book hit #1 in 15 Amazon categories.',
    body:
      'Not because the book was better than everyone else\'s. Because we treated distribution like a real problem worth solving, not an afterthought.',
  },
  {
    head: '2021, Founded Audio & Co.®',
    body:
      'After watching too many friends launch podcasts that died in six weeks because nobody had time to edit, publish, repurpose, and promote. We built the engine so they didn\'t have to.',
  },
  {
    head: '2025, Launched PopCrown, a PWA reaching 130+ languages.',
    body:
      'We know how to build audiences from zero, in every corner of the internet. That knowledge sits behind every Pro Plan we run.',
  },
  {
    head: '2026, Niched down to the Pro Plan, and layered on fully SEO-packed websites.',
    body:
      'One weekly recording turns into 12 published outputs (podcast, YouTube, Shorts, blog, thumbnails, SEO), and we now build the fully SEO-optimised website underneath it too. Complete visibility package: website, SEO, podcast and YouTube repurposing, under one roof. Everything is still available as a standalone service if that is all you need, but for the first time we can take care of the entire visibility stack in-house.',
  },
];

export default function About() {
  return (
    <main className="font-body text-[#475569] bg-[#F6F8FB]">
      <SEO
        title="Jo Day, Founder of Audio & Co. | About Us"
        description="Meet Jo Day, founder of Audio & Co. UK podcast management, book publishing and SEO websites for coaches, consultants and authors."
        keywords={[
          'jo day audio and co',
          'audio and co founder',
          'independent content studio',
          'podcast production',
          'content strategist',
          'pro plan podcast production',
        ]}
        url="https://audioandco.com/about"
      />

      {/* HERO, leads with WHY, not with "Hi, I'm Jo". */}
      <Section bg="bg" innerClassName="!py-16 sm:!py-24" testId="about-hero">
        <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-3">
            <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">
              About Audio &amp; Co.
            </p>
            <h1
              className="font-display font-bold text-[#1E2A44] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-3"
              data-testid="about-h1"
            >
              We built this because brilliant business owners were <span className="text-[#BC516F]">disappearing behind their own noise</span>.
            </h1>
            <p className="text-lg text-[#475569] mt-6 leading-relaxed max-w-2xl">
              You started your business because you&rsquo;re brilliant at what you do, not because you wanted
              to become a full-time podcast editor, YouTube uploader, SEO analyst and social media manager.
              We fix that. One weekly recording. Twelve published outputs. Every week. Stay for four weeks or six months — most stay six because that&rsquo;s when it compounds.
              That&rsquo;s the whole promise.
            </p>
            <div className="mt-8">
              <CTABlock size="lg" testIdPrefix="about-cta" />
            </div>
          </div>
          <div className="lg:col-span-2">
            <div className="relative max-w-sm mx-auto lg:max-w-none">
              <img
                src={JO_PHOTO}
                alt="Jo Day, founder of Audio & Co., UK specialist in podcast management, book publishing and websites built for SEO"
                title="Jo Day, Founder | Audio & Co."
                className="w-full aspect-[4/5] object-cover object-top rounded-[14px] border border-[#E2E8F0] shadow-[0_10px_30px_rgba(30,42,68,0.12)]"
                loading="eager"
                data-testid="about-jo-photo"
              />
              <div className="absolute -bottom-4 -right-4 bg-[#BC516F] text-white px-4 py-2 rounded-lg font-display font-bold text-sm shadow-lg">
                Independent · Remote worldwide
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* THE PEOPLE, "People buy from people they know, like, and trust." */}
      <Section bg="white" testId="about-people">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div>
            <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">
              The person behind it
            </p>
            <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2">
              Hi, I&rsquo;m Jo Day, founder of Audio &amp; Co.
            </h2>
            <p className="text-[#475569] mt-5 leading-relaxed">
              I run multiple businesses, Audio &amp; Co.®, PopCrown, and Brunchi.shop ,
              and I&rsquo;ve been getting up and doing the do every single day for sixteen years.
            </p>
            <p className="text-[#475569] mt-4 leading-relaxed">
              We know the hustle. We know the sacrifices. We know
              what it&rsquo;s like to look at a Sunday-night to-do list and wonder how anyone
              running their own show is supposed to make time for content on top of everything
              else. Our clients are usually in exactly the same boat, and that&rsquo;s the whole
              reason we exist.
            </p>
            <p className="text-[#475569] mt-4 leading-relaxed">
              Everything we build for Pro Plan clients is designed to work
              <em> for </em>you, not against you. We do the production. You get the visibility.
              You keep running your actual business.
            </p>
            <div className="mt-6 rounded-[14px] border-l-4 border-[#BC516F] bg-white p-5 shadow-[0_8px_20px_rgba(30,42,68,0.04)]" data-testid="about-referral-note">
              <p className="text-[#1E2A44] font-display font-semibold text-lg leading-snug">
                Most of our clients come from referrals and word of mouth.
              </p>
              <p className="text-[#475569] mt-2 leading-relaxed">
                In sixteen years, we have never spent a single penny on paid marketing.
                That&rsquo;s how we keep our costs so competitive, and how we pass those
                savings straight on to our clients.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {TRUST.map((c, i) => {
              const Icon = c.icon;
              return (
                <div
                  key={i}
                  className="bg-[#F6F8FB] border border-[#E2E8F0] rounded-[14px] p-6"
                  data-testid={`about-trust-${i}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-white border border-[#E2E8F0] flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#BC516F]" />
                  </div>
                  <h3 className="font-display font-semibold text-[#1E2A44] text-base leading-snug">
                    {c.t}
                  </h3>
                  <p className="text-[#64748B] text-sm mt-1.5 leading-relaxed">{c.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* HOW WE GOT HERE, narrative story beats, not a corporate CV. */}
      <Section bg="bg" testId="about-story">
        <div className="max-w-2xl">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">
            How we got here
          </p>
          <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2">
            Every good business exists because someone got tired of watching a problem go unfixed.
          </h2>
          <p className="text-[#475569] mt-5 leading-relaxed">
            This one is no different. Here&rsquo;s the short version.
          </p>
        </div>

        <ol className="mt-12 space-y-5 max-w-3xl">
          {STORY_BEATS.map((s, i) => (
            <li
              key={i}
              className="bg-white border border-[#E2E8F0] rounded-[14px] p-6 sm:p-7 shadow-[0_4px_16px_rgba(30,42,68,0.04)]"
              data-testid={`about-story-${i}`}
            >
              <h3 className="font-display font-semibold text-[#1E2A44] text-lg sm:text-xl leading-snug">
                {s.head}
              </h3>
              <p className="text-[#475569] mt-2 leading-relaxed">{s.body}</p>
            </li>
          ))}
        </ol>
      </Section>

      {/* WE PRACTICE WHAT WE PREACH */}
      <Section bg="white" testId="about-practice">
        <div className="max-w-3xl">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Practice what we preach</p>
          <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2">
            Jo&rsquo;s own businesses. Built and run by us.
          </h2>
          <p className="text-[#475569] mt-4 leading-relaxed text-[17px]">
            Alongside Audio &amp; Co., Jo builds and runs the businesses below. These are <strong>ours</strong>, not client work. Every visibility tactic on this site is one we ship on them first, then bring to clients only once we&rsquo;ve proved it works.
          </p>
          <p className="text-[#64748B] mt-3 text-[15px]">
            Looking for the client work? Podcasts we produce, sites we build, books we publish, and audiobooks we ship all live on the <Link to="/results" className="text-[#BC516F] hover:text-[#A7468A] font-semibold">Results page</Link>.
          </p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {PORTFOLIO_ITEMS.filter((it) => it.kind === 'in-house').map((it) => (
            <a
              key={it.id}
              href={it.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group block bg-[#F6F8FB] border border-[#E2E8F0] rounded-[14px] overflow-hidden shadow-[0_6px_18px_rgba(30,42,68,0.04)] hover:shadow-[0_18px_44px_rgba(30,42,68,0.12)] transition"
              data-testid={`about-practice-${it.id}`}
            >
              <div className="aspect-[16/10] overflow-hidden bg-white">
                <img src={it.img} alt={it.alt} title={it.title} loading="lazy" decoding="async" className="w-full h-full object-cover object-top transition duration-500 group-hover:scale-[1.03]" />
              </div>
              <div className="p-4">
                <p className="text-[11px] uppercase tracking-widest text-[#1E2A44] font-semibold">
                  Jo&rsquo;s own business
                </p>
                <h3 className="font-display font-semibold text-[#1E2A44] mt-1 leading-tight">{it.name}</h3>
                <p className="text-[#475569] text-sm mt-1 leading-snug">{it.tagline}</p>
              </div>
            </a>
          ))}
          {/* Audio & Co. itself, so the grid reads as "the three things Jo owns and runs today" */}
          <a
            href="https://audioandco.com/"
            className="group block bg-[#1E2A44] text-white rounded-[14px] overflow-hidden shadow-[0_6px_18px_rgba(30,42,68,0.12)] hover:shadow-[0_18px_44px_rgba(30,42,68,0.22)] transition"
            data-testid="about-practice-audioandco"
          >
            <div className="aspect-[16/10] flex items-center justify-center bg-gradient-to-br from-[#1E2A44] via-[#243149] to-[#0F1729] px-6">
              <p className="font-display font-bold text-white text-2xl sm:text-3xl leading-tight text-center tracking-tight">Audio &amp; Co.<sup className="text-[#BC516F]">®</sup></p>
            </div>
            <div className="p-4">
              <p className="text-[11px] uppercase tracking-widest text-[#BC516F] font-semibold">
                Jo&rsquo;s own business
              </p>
              <h3 className="font-display font-semibold mt-1 leading-tight">Audio &amp; Co.®</h3>
              <p className="text-white/70 text-sm mt-1 leading-snug">The studio you&rsquo;re on right now. Since 2021.</p>
            </div>
          </a>
        </div>
        <p className="text-sm text-[#64748B] mt-6 text-center">
          More on custom website and app builds on <Link to="/websites-apps" className="text-[#BC516F] hover:text-[#A7468A] font-semibold">/websites-apps</Link>.
        </p>
      </Section>

      {/* WHO WE WORK WITH, story → customer bridge */}
      <Section bg="white" testId="about-for-you">
        <div className="max-w-3xl">
          <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">
            Who this is for
          </p>
          <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2">
            You, if any of this sounds familiar.
          </h2>
        </div>
        <ul className="mt-10 grid md:grid-cols-2 gap-4 max-w-4xl">
          {[
            "You're brilliant at your craft, the marketing side eats your Sundays.",
            "You've launched a podcast/newsletter/YouTube before and it fizzled after week six.",
            "Your website converts, but not enough people are finding it.",
            "You know content is the answer, you just don't have another 15 hours a week.",
            "You've been burnt by agencies who charged £4k+ and gave you a logo refresh.",
            "You want a team who actually picks up the phone.",
          ].map((line, i) => (
            <li
              key={i}
              className="flex items-start gap-3 bg-[#F6F8FB] border border-[#E2E8F0] rounded-[14px] p-5"
              data-testid={`about-for-you-${i}`}
            >
              <span className="w-6 h-6 rounded-full bg-[#BC516F]/10 text-[#BC516F] font-bold text-sm flex items-center justify-center shrink-0 mt-0.5">
                ✓
              </span>
              <span className="text-[#1E2A44] leading-relaxed">{line}</span>
            </li>
          ))}
        </ul>
      </Section>

      {/* JO'S APPROACH + CONNECT */}
      <Section bg="bg" testId="about-approach">
        <div className="grid lg:grid-cols-[1.3fr_0.7fr] gap-10 items-start">
          <div>
            <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">How Jo works</p>
            <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight mt-2">Jo&rsquo;s approach to podcast, book and website projects.</h2>
            <ul className="mt-6 space-y-4 text-[#475569] leading-relaxed">
              <li><strong className="text-[#1E2A44]">Record once, publish everywhere.</strong> One weekly recording becomes your podcast, YouTube, Shorts, articles and website updates, so your time goes further.</li>
              <li><strong className="text-[#1E2A44]">Own your platform.</strong> Social reach comes and goes. Your website, your book and your back catalogue keep working for you.</li>
              <li><strong className="text-[#1E2A44]">Real people, one point of contact.</strong> Human editors and a team that knows your business, not a ticket queue.</li>
              <li><strong className="text-[#1E2A44]">Earn trust, don&rsquo;t buy it.</strong> In sixteen years Audio &amp; Co. has grown through referrals and word of mouth, never paid marketing.</li>
            </ul>
          </div>
          <div className="rounded-[14px] bg-[#1E2A44] text-white p-7" data-testid="about-connect">
            <h2 className="font-display font-bold text-2xl">Connect with Jo Day</h2>
            <p className="text-white/75 mt-2 text-[15px]">Questions about a podcast, book or website? Jo replies personally.</p>
            <a href="mailto:hello@audioandco.com" className="mt-5 block font-semibold text-[#E7A3C4] hover:text-white" data-testid="about-connect-email">hello@audioandco.com</a>
            <Link to="/contact" className="mt-3 inline-block font-semibold text-[#E7A3C4] hover:text-white" data-testid="about-connect-contact">Send Jo a message</Link>
            <Link to="/book" className="mt-5 block text-center rounded-full bg-gradient-to-r from-[#A7468A] to-[#BC516F] px-5 py-3 font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5" data-testid="about-connect-book">Book a free 30-minute call</Link>
          </div>
        </div>
      </Section>

      {/* LATEST FROM THE BLOG */}
      <LatestFromBlog
        eyebrow="Read next"
        heading="Straight-talking guides"
        subheading="If you'd rather try some of this yourself first, start here."
        bg="bg"
        testId="about-latest-blog"
      />

      {/* FINAL CTA */}
      <Section bg="navy" innerClassName="!py-16">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-tight">
            Want us on your content team?
          </h2>
          <p className="text-white/75 mt-4 text-lg">
            30 minutes. We&rsquo;ll see if the Pro Plan is right for your business, and if it&rsquo;s not, we&rsquo;ll tell you honestly.
          </p>
          <div className="mt-7 flex flex-col items-center gap-4">
            <CTABlock size="lg" align="center" invert testIdPrefix="about-final-cta" />
            <Link to="/training" className="text-sm text-white/70 hover:text-white transition">
              Looking for the 6-week training programme?
            </Link>
          </div>
        </div>
      </Section>
    </main>
  );
}
